import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  alpha,
  Fade,
  Typography,
  Avatar,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Dashboard,
  CalendarToday,
  Assignment,
  Receipt,
  AttachMoney,
  LocalPharmacy,
  AccountCircle,
  Business,
  Message,
  Notifications,
  AssignmentTurnedIn,
  BarChart,
  GroupAdd,
  Feedback,
  ExpandLess,
  ExpandMore,
  People,
  MedicalServices,
  Psychology,
  Healing,
  Settings,
  Subscriptions,
  Person,
  Description,
  History,
  ContactEmergency,
  Note,
  Folder,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';

const StyledListItem = styled(ListItem)(({ theme }) => ({
  borderRadius: 12,
  marginBottom: theme.spacing(0.5),
  '&.Mui-selected': {
    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    color: 'white',
    '& .MuiListItemIcon-root': {
      color: 'white',
    },
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
  },
}));

const getMenuItems = (role) => {
  switch (role) {
    case 'patient':
      return [
        {
          id: 'overview',
          title: 'Overview',
          path: '/patient-dashboard',
          icon: <Dashboard />,
        },
        {
          id: 'appointments',
          title: 'Appointments',
          path: '/patient-dashboard/appointments',
          icon: <CalendarToday />,
        },
        {
          id: 'consultations',
          title: 'Consultations',
          icon: <Psychology />,
          subItems: [
            {
              title: 'History',
              path: '/patient-dashboard/consultation-history',
            },
            {
              title: 'Medical Records',
              path: '/patient-dashboard/medical-records',
            },
          ],
        },
        {
          id: 'billing',
          title: 'Billing',
          path: '/patient-dashboard/billing',
          icon: <AttachMoney />,
        },
        {
          id: 'messages',
          title: 'Messages',
          path: '/patient-dashboard/messages',
          icon: <Message />,
        },
        {
          id: 'support',
          title: 'Support',
          path: '/patient-dashboard/support',
          icon: <Healing />,
        },
        {
          id: 'feedback',
          title: 'Feedback',
          path: '/patient-dashboard/feedback',
          icon: <Feedback />,
        },
        {
          id: 'feedback-p0',
          title: 'Report Issue',
          path: '/patient-dashboard/feedback-p0',
          icon: <Feedback />,
        },
        {
          id: 'support-tickets',
          title: 'Support Tickets',
          path: '/patient-dashboard/support-tickets',
          icon: <Healing />,
        },
      ];

    case 'doctor':
      return [
        {
          id: 'overview',
          title: 'Overview',
          path: '/doctor-dashboard',
          icon: <Dashboard />,
        },
        {
          id: 'appointments',
          title: 'Appointments',
          icon: <CalendarToday />,
          subItems: [
            {
              title: 'All Appointments',
              path: '/doctor-dashboard/appointments',
            },
            {
              title: 'Search Appointments',
              path: '/doctor-dashboard/appointments/search',
            },
            {
              title: 'Appointment History',
              path: '/doctor-dashboard/appointments/history',
            },
          ],
        },
        {
          id: 'patients',
          title: 'Patients',
          icon: <People />,
          subItems: [
            {
              title: 'Patient List',
              path: '/doctor-dashboard/patients',
            },
            {
              title: 'Clinical Notes',
              path: '/doctor-dashboard/patients/:id/clinical-notes',
            },
            {
              title: 'Emergency Contacts',
              path: '/doctor-dashboard/patients/:id/emergency-contacts',
            },
            {
              title: 'Mental Health Assessment',
              path: '/doctor-dashboard/patients/:id/mental-health-assessment',
            },
            {
              title: 'Patient Documents',
              path: '/doctor-dashboard/patients/:id/documents',
            },
            {
              title: 'Intake Form',
              path: '/doctor-dashboard/patients/:id/intake-form',
            },
            {
              title: 'Treatment History',
              path: '/doctor-dashboard/patients/:id/treatment-history',
            },
            {
              title: 'Treatment Plan',
              path: '/doctor-dashboard/patients/:id/treatment-plan',
            },
          ],
        },
        {
          id: 'consultations',
          title: 'Consultations',
          icon: <MedicalServices />,
          subItems: [
            {
              title: 'Dashboard',
              path: '/doctor-dashboard/consultation-dashboard',
            },
            {
              title: 'Enhanced Consultation',
              path: '/doctor-dashboard/enhanced-consultation',
            },
            {
              title: 'Active',
              path: '/doctor-dashboard/consultation',
            },
            {
              title: 'History',
              path: '/doctor-dashboard/consultation-history',
            },
          ],
        },
        // {
        //   id: 'prescriptions',
        //   title: 'Prescribe',
        //   path: '/doctor-dashboard/prescribe',
        //   icon: <Receipt />,
        // },
        {
          id: 'availability',
          title: 'Availability',
          path: '/doctor-dashboard/availability',
          icon: <CalendarToday />,
        },
        // {
        //   id: 'clinic',
        //   title: 'Clinic',
        //   path: '/doctor-dashboard/clinic',
        //   icon: <Business />,
        // },
        {
          id: 'billing',
          title: 'Billing',
          path: '/doctor-dashboard/billing',
          icon: <AttachMoney />,
        },
        {
          id: 'clinic-overview',
          title: 'Clinic Overview',
          path: '/doctor-dashboard/clinic-overview',
          icon: <Business />,
        },
        {
          id: 'feedback',
          title: 'Feedback',
          path: '/doctor-dashboard/feedback',
          icon: <Feedback />,
        },
        {
          id: 'support',
          title: 'Support Tickets',
          path: '/doctor-dashboard/support-tickets',
          icon: <Healing />,
        },
      ];

    case 'pharmacist':
      return [
        {
          id: 'overview',
          title: 'Overview',
          path: '/pharmacist-dashboard',
          icon: <Dashboard />,
        },
        {
          id: 'inventory',
          title: 'Inventory',
          path: '/pharmacist-dashboard/inventory',
          icon: <LocalPharmacy />,
        },
        {
          id: 'prescriptions',
          title: 'Prescriptions',
          path: '/pharmacist-dashboard/prescriptions',
          icon: <Receipt />,
        },
        {
          id: 'stock',
          title: 'Stock',
          path: '/pharmacist-dashboard/stock',
          icon: <Business />,
        },
        {
          id: 'reports',
          title: 'Reports',
          path: '/pharmacist-dashboard/reports',
          icon: <BarChart />,
        },
        {
          id: 'messages',
          title: 'Messages',
          path: '/pharmacist-dashboard/messages',
          icon: <Message />,
        },
      ];

    case 'receptionist':
      return [
        {
          id: 'overview',
          title: 'Overview',
          path: '/receptionist-dashboard',
          icon: <Dashboard />,
        },
        {
          id: 'appointments',
          title: 'Appointments',
          path: '/receptionist-dashboard/appointments',
          icon: <CalendarToday />,
        },
        {
          id: 'check-in',
          title: 'Patient Check-in',
          path: '/receptionist-dashboard/check-in',
          icon: <AssignmentTurnedIn />,
        },
        {
          id: 'registration',
          title: 'Patient Registration',
          path: '/receptionist-dashboard/registration',
          icon: <GroupAdd />,
        },
        {
          id: 'billing',
          title: 'Billing',
          path: '/receptionist-dashboard/billing',
          icon: <AttachMoney />,
        },
        {
          id: 'communication',
          title: 'Communication',
          path: '/receptionist-dashboard/communication',
          icon: <Message />,
        },
        {
          id: 'tasks',
          title: 'Task Management',
          path: '/receptionist-dashboard/tasks',
          icon: <Assignment />,
        },
        {
          id: 'clinic-overview',
          title: 'Clinic Overview',
          path: '/receptionist-dashboard/clinic-overview',
          icon: <Business />,
        },
        {
          id: 'feedback',
          title: 'Feedback',
          path: '/receptionist-dashboard/feedback',
          icon: <Feedback />,
        },
        {
          id: 'support',
          title: 'Support Tickets',
          path: '/receptionist-dashboard/support-tickets',
          icon: <Healing />,
        },
      ];

    default:
      return [];
  }
};

const Sidebar = ({ role }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openSubMenu, setOpenSubMenu] = useState('');
  const { user } = useSelector((state) => state.auth);
  const menuItems = getMenuItems(role);

  const handleNavigate = (path) => {
    navigate(path);
  };

  const handleSubMenuClick = (id) => {
    setOpenSubMenu(openSubMenu === id ? '' : id);
  };

  const isSelected = (path) => {
    return location.pathname === path;
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* User Profile Section */}
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Avatar
          sx={{
            width: 80,
            height: 80,
            margin: '0 auto',
            mb: 2,
            background: (theme) =>
              `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          }}
        >
          {user?.name?.charAt(0) || 'U'}
        </Avatar>
        <Typography variant='h6' gutterBottom>
          {user?.name || 'User'}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          {role.charAt(0).toUpperCase() + role.slice(1)}
        </Typography>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Navigation Menu */}
      <List sx={{ px: 2, flexGrow: 1 }}>
        {menuItems.map((item) => (
          <React.Fragment key={item.id}>
            <StyledListItem
              button
              selected={isSelected(item.path)}
              onClick={() => {
                if (item.subItems) {
                  handleSubMenuClick(item.id);
                } else {
                  handleNavigate(item.path);
                }
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.title} />
              {item.subItems && (
                <>{openSubMenu === item.id ? <ExpandLess /> : <ExpandMore />}</>
              )}
            </StyledListItem>

            {item.subItems && (
              <Collapse
                in={openSubMenu === item.id}
                timeout='auto'
                unmountOnExit
              >
                <List component='div' disablePadding>
                  {item.subItems.map((subItem, index) => (
                    <StyledListItem
                      key={index}
                      button
                      selected={isSelected(subItem.path)}
                      onClick={() => handleNavigate(subItem.path)}
                      sx={{ pl: 4 }}
                    >
                      <ListItemText primary={subItem.title} />
                    </StyledListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>

      {/* Settings Section */}
      <Divider sx={{ mt: 2 }} />
      <List sx={{ px: 2 }}>
        <StyledListItem
          button
          onClick={() => handleNavigate(`/${role}-dashboard/settings`)}
        >
          <ListItemIcon>
            <Settings />
          </ListItemIcon>
          <ListItemText primary='Settings' />
        </StyledListItem>
        {role === 'doctor' && (
          <StyledListItem
            button
            onClick={() => handleNavigate('/doctor-dashboard/settings/reminders')}
          >
            <ListItemIcon>
              <Notifications />
            </ListItemIcon>
            <ListItemText primary='Reminder Settings' />
          </StyledListItem>
        )}
      </List>
    </Box>
  );
};

export default Sidebar;
